import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, OnChanges, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
// La tabla de controles ya no va en acordeón.
// import { Accordion } from '@shared/components/ui/accordion/accordion';
import { ButtonComponent } from '@shared/components/ui/button/button.component';
import { DataTable, TableCellDirective, TableColumn, TableRowDetailDirective } from '@shared/components/ui/list-components';
import { InputFieldComponent } from '@shared/components/form/input/input-field.component';
import { LabelComponent } from '@shared/components/form/label/label.component';
import { SelectFieldComponent, SelectOption } from '@shared/components/form/select/select-field.component';
import { ErrorService } from '@shared/services/error.service';
import { SnackbarService } from '@shared/services/snackbar.service';
import { etiquetaVaca } from '@shared/utils/etiqueta-vaca';
import { sumarDiasLocal } from '@shared/utils/fecha-local';
import { CultivoEmbrionario, CultivoEmbrionarioControlPayload, SalidaCultivoEmbrionario, TecnicaCongelamiento } from '../cultivos.interface';
import { CampoControlCultivo, esEtapaProduccion, estadoCampoControl, MATRIZ_CONTROL_CULTIVO, TurnoControlCultivo } from '../cultivos.matriz';
import { controlDeEtapa, porcentajeProduccion, totalProducido } from '../cultivos-planilla';
import { ProduccionEtapa } from './produccion-etapa.model';
import { ProduccionEtapaPanel } from './produccion-etapa';
import { CultivosService } from '../cultivos.service';
import { SalidasFrescas } from '../../produccion/salidas-frescas';
import { crearSalidaFresca, FilaSalidaFresca, frescoPendiente } from '../../produccion/salidas-frescas.model';
import { SalidasFrescasService } from '../../produccion/salidas-frescas.service';

interface FilaControl {
  id: string;
  dia: number;
  turno: TurnoControlCultivo | null;
  nombre: string;
  form: FormGroup;
  error: string;
  /** Solo en PM6, AM7 y PM7: la producción de embriones de esa etapa. */
  produccion?: ProduccionEtapa;
}

@Component({
  selector: 'app-planilla-cultivo',
  imports: [DatePipe, DecimalPipe, ReactiveFormsModule, /* Accordion, */ ButtonComponent, DataTable,
    TableCellDirective, TableRowDetailDirective, InputFieldComponent, LabelComponent, SelectFieldComponent,
    ProduccionEtapaPanel, SalidasFrescas],
  templateUrl: './planilla-cultivo.html',
})
export class PlanillaCultivo implements OnChanges {
  @Input({ required: true }) cultivo!: CultivoEmbrionario;
  @Input({ required: true }) idAgendamiento!: number;
  @Input() canCreate = false;
  @Input() canEdit = false;
  @Input() canDelete = false;
  @Input() estadiosOptions: SelectOption[] = [];
  @Input() tecnicasCongelamiento: TecnicaCongelamiento[] = [];
  @Input() opsPuntosOptions: SelectOption[] = [];
  @Input() opsColoresOptions: SelectOption[] = [];
  @Output() saved = new EventEmitter<void>();
  filas: FilaControl[] = [];
  frescos: FilaSalidaFresca[] = [];
  /**
   * Filas de la tabla de controles: desde el día 6. Cultivados (día 1) y
   * clivados (día 4) se cargan en la fila resumen de arriba, que usa el
   * formulario de esas mismas filas: por eso siguen en `filas`.
   */
  filasTabla: FilaControl[] = [];
  saving = false;
  readonly observacion = new FormControl('');
  errorObservacion = '';
  readonly etiquetaVaca = etiquetaVaca;
  readonly porcentajeProduccion = porcentajeProduccion;
  readonly controlDeEtapa = controlDeEtapa;
  readonly totalProducido = totalProducido;
  /** Filas con el desplegable de producción abierto; sobrevive a los recálculos. */
  readonly expandidas = new Set<string>();
  // readonly expandida = (fila: FilaControl): boolean => !!fila.produccion && this.expandidas.has(fila.id);
  readonly expandida = (fila: FilaControl): boolean => !!fila.produccion && (this.siempreAbierta(fila) || this.expandidas.has(fila.id));

  /** AM7 y PM7 muestran la producción sin desplegable; PM6 la sigue abriendo a pedido. */
  siempreAbierta(fila: FilaControl): boolean {
    return fila.dia === 7;
  }
  readonly campos: CampoControlCultivo[] = ['cantidad', 'prevision', 'param_estadio_embrionario_id', 'grado'];
  // private readonly camposDeProduccion: CampoControlCultivo[] = ['cantidad', 'prevision'];
  readonly columns: TableColumn[] = [
    { key: 'donante', header: 'Donante' }, { key: 'raza', header: 'Raza' },
    { key: 'toro', header: 'Toro' }, { key: 'viables', header: 'Ovocitos viables' },
    { key: 'ovocitos', header: 'Ovocitos totales' },
    { key: 'cultivados', header: 'Ovocitos cultivados' }, { key: 'clivados', header: 'Clivados' },
    { key: 'totales', header: 'Totales' }, { key: 'porcentaje', header: '% de producción' },
    // Sin observaciones en el resumen: se editan en "Información del cultivo".
    // { key: 'observaciones', header: 'Observaciones' },
  ];
  readonly controlColumns: TableColumn[] = [
    { key: 'nombre', header: 'Control' }, { key: 'fecha', header: 'Fecha' },
    // Cantidad solo la usaban cultivados y clivados, que ahora se cargan en la fila resumen.
    // { key: 'cantidad', header: 'Cantidad' }, { key: 'prevision', header: 'Previsión' },
    { key: 'prevision', header: 'Previsión' },
    { key: 'param_estadio_embrionario_id', header: 'Estadio' }, { key: 'grado', header: 'Grado' },
    // Sin observaciones para ahorrar espacio: el valor guardado se conserva al editar.
    // { key: 'observacion', header: 'Observaciones' },
    { key: 'produccion', header: 'Producción' },
  ];
  private service = inject(CultivosService);
  private frescosService = inject(SalidasFrescasService);
  private errors = inject(ErrorService);
  private snackbar = inject(SnackbarService);

  get pendientes(): number {
    return this.filas.filter(f => f.form.dirty).length + this.filas.filter(f => f.produccion?.pendiente).length
      + this.frescos.filter(frescoPendiente).length
      + (this.observacion.dirty ? 1 : 0);
  }

  get tecnicasCongelamientoOptions(): SelectOption[] {
    return this.tecnicasCongelamiento.map(t => ({
      value: t.id,
      label: t.codigo === 'DT' ? t.codigo : `${t.codigo} - ${t.nombre}`,
    }));
  }

  get cultivados(): FilaControl | undefined { return this.filas.find(f => f.dia === 1); }
  get clivados(): FilaControl | undefined { return this.filas.find(f => f.dia === 4); }

  ngOnChanges(): void {
    // Una actualización de producción no debe borrar controles aún sin guardar.
    if (!this.pendientes && !this.saving) this.restablecer();
  }

  permitido(fila: FilaControl): boolean {
    return controlDeEtapa(this.cultivo, fila.dia, fila.turno) ? this.canEdit : this.canCreate;
  }

  toggleProduccion(fila: FilaControl): void {
    this.expandidas.has(fila.id) ? this.expandidas.delete(fila.id) : this.expandidas.add(fila.id);
  }

  visible(fila: FilaControl, campo: CampoControlCultivo): boolean {
    // En PM6, AM7 y PM7 lo producido se carga en la producción de la etapa:
    // la cantidad no se muestra en la fila. La previsión queda solo en PM6.
    // if (fila.produccion && this.camposDeProduccion.includes(campo)) return false;
    if (fila.produccion && campo === 'cantidad') return false;
    if (fila.produccion && fila.dia === 7 && campo === 'prevision') return false;
    if (this.enPanel(fila, campo)) return false;
    return estadoCampoControl(fila.dia, campo) !== 'prohibido';
  }

  /** AM7/PM7: estadio y grado se cargan en el bloque de producción, no en la fila. */
  enPanel(fila: FilaControl, campo: CampoControlCultivo): boolean {
    return this.siempreAbierta(fila) && (campo === 'param_estadio_embrionario_id' || campo === 'grado');
  }

  /** Oculto en la fila pero admitido por la matriz: se reenvía lo guardado para no borrarlo. */
  private ocultoEnFila(fila: FilaControl, campo: CampoControlCultivo): boolean {
    // return !this.visible(fila, campo) && estadoCampoControl(fila.dia, campo) !== 'prohibido';
    return !this.visible(fila, campo) && !this.enPanel(fila, campo) && estadoCampoControl(fila.dia, campo) !== 'prohibido';
  }

  restablecer(): void {
    if (this.saving || !this.cultivo) return;
    this.observacion.reset(this.cultivo.observacion ?? '');
    this.canEdit ? this.observacion.enable() : this.observacion.disable();
    this.errorObservacion = '';
    const nombres: Record<number, string> = { 1: 'Ovocitos cultivados', 4: 'Clivados', 6: 'Previsión', 7: 'Congelamiento', 8: 'Descarte' };
    // Los días 2, 3 y 5 no están en el relevamiento: se ocultan de la planilla.
    // La matriz los sigue admitiendo y lo ya cargado aparece en el historial.
    // Para volver a mostrarlos, reponer la línea comentada y quitar el filtro.
    // this.filas = MATRIZ_CONTROL_CULTIVO.flatMap(config =>
    // El día 8 (descarte) también se oculta por ahora: falta confirmar con el
    // laboratorio si ese día se registra algo. Para reponerlo, volver a esta línea.
    // this.filas = MATRIZ_CONTROL_CULTIVO.filter(config => ![2, 3, 5].includes(config.dia)).flatMap(config =>
    this.filas = MATRIZ_CONTROL_CULTIVO.filter(config => ![2, 3, 5, 8].includes(config.dia)).flatMap(config =>
      (config.turnos.length ? config.turnos : [null]).map(turno => {
        const existente = controlDeEtapa(this.cultivo, config.dia, turno);
        const controls: Record<string, FormControl> = {
          fecha_control: new FormControl(existente?.fecha_control ?? sumarDiasLocal(this.cultivo.fecha_inicio, config.dia), Validators.required),
          observacion: new FormControl(existente?.observacion ?? ''),
        };
        for (const campo of this.campos) {
          const validators = [Validators.pattern(/^\d+$/), Validators.min(campo === 'grado' ? 1 : 0)];
          if (campo === 'grado') validators.push(Validators.max(3));
          if (estadoCampoControl(config.dia, campo) === 'obligatorio') validators.push(Validators.required);
          controls[campo] = new FormControl(existente?.[campo] ?? null, validators);
        }
        const fila: FilaControl = {
          id: `${config.dia}-${turno ?? ''}`, dia: config.dia, turno,
          // Nomenclatura del relevamiento para las etapas con turno: PM6, AM7, PM7.
          // nombre: `${nombres[config.dia] ?? `Control ${config.dia}`}${turno ? ` ${turno}${config.dia}` : ''}`,
          // Más descriptivo: "Congelamiento AM día 7".
          nombre: `${nombres[config.dia] ?? `Control ${config.dia}`}${turno ? ` ${turno} día ${config.dia}` : ''}`,
          form: new FormGroup(controls), error: '',
        };
        if (!this.permitido(fila)) fila.form.disable();
        if (turno && esEtapaProduccion(config.dia, turno)) {
          fila.produccion = new ProduccionEtapa(config.dia, turno, sumarDiasLocal(this.cultivo.fecha_inicio, config.dia) ?? '', () => this.tecnicasCongelamiento);
          const salida = this.salidaDe(config.dia, turno);
          fila.produccion.restablecer(salida, salida ? this.canEdit : this.canCreate);
        }
        return fila;
      }));
    this.filasTabla = this.filas.filter(f => f.dia >= 6);
    this.frescos = this.cultivo.salidas_cultivo.filter(s => s.destino === 'F')
      .map(s => crearSalidaFresca(this.cultivo, s, this.canEdit));
    if (!this.frescos.length && this.canCreate) this.agregarFresco();
  }

  agregarFresco(): void {
    this.frescos.push(crearSalidaFresca(this.cultivo, null, this.canCreate));
  }

  async eliminarFresco(fila: FilaSalidaFresca): Promise<void> {
    if (this.saving) return;
    this.saving = true;
    try {
      if (!await this.frescosService.eliminar(fila, this.idAgendamiento)) return;
      this.frescos = this.frescos.filter(f => f !== fila);
      if (fila.salida) this.saved.emit();
    } finally {
      this.saving = false;
    }
  }

  async guardar(): Promise<void> {
    if (this.saving || !this.pendientes) return;
    const pendientes = this.filas.filter(f => f.form.dirty && this.permitido(f));
    const validas = pendientes.filter(f => {
      f.error = '';
      f.form.markAllAsTouched();
      if (f.form.invalid) f.error = 'Complete los campos obligatorios con cantidades enteras válidas.';
      return f.form.valid;
    });
    this.saving = true;
    this.observacion.disable({ emitEvent: false });
    this.filas.forEach(f => f.form.disable({ emitEvent: false }));
    let guardadas = 0;
    try {
      if (this.observacion.dirty && this.canEdit) {
        try {
          const response = await firstValueFrom(this.service.update(this.idAgendamiento, this.cultivo.id, {
            fecha_inicio: this.cultivo.fecha_inicio,
            param_solucion_id: this.cultivo.param_solucion_id,
            observacion: this.observacion.value || null,
          }));
          if (response.status) {
            this.cultivo.observacion = response.data.observacion;
            this.observacion.markAsPristine();
            this.errorObservacion = '';
            guardadas++;
          } else this.errorObservacion = 'No se pudieron guardar las observaciones.';
        } catch (error) {
          this.errorObservacion = 'No se pudieron guardar las observaciones.';
          if (error instanceof HttpErrorResponse) this.errors.handleError(error);
        }
      }
      for (const fila of validas) {
        const existente = controlDeEtapa(this.cultivo, fila.dia, fila.turno);
        const raw = fila.form.getRawValue();
        const payload: CultivoEmbrionarioControlPayload = {
          dia: fila.dia, turno: fila.turno, fecha_control: raw.fecha_control,
          observacion: raw.observacion || null,
          clivaje: this.visible(fila, 'clivaje') ? existente?.clivaje ?? null : null,
        };
        for (const campo of this.campos) {
          if (campo === 'clivaje') continue;
          // payload[campo] = this.visible(fila, campo) && raw[campo] !== null && raw[campo] !== '' ? Number(raw[campo]) : null;
          payload[campo] = this.ocultoEnFila(fila, campo)
            ? (existente?.[campo] as number | null | undefined) ?? null
            : (this.visible(fila, campo) || this.enPanel(fila, campo)) && raw[campo] !== null && raw[campo] !== '' ? Number(raw[campo]) : null;
        }
        try {
          const response = await firstValueFrom(existente
            ? this.service.updateControl(this.idAgendamiento, this.cultivo.id, existente.id, payload)
            : this.service.storeControl(this.idAgendamiento, this.cultivo.id, payload));
          if (!response.status) { fila.error = 'No se pudo guardar. Los cambios siguen pendientes.'; continue; }
          this.cultivo.controles = [...this.cultivo.controles.filter(c => c.id !== response.data.id), response.data];
          fila.form.markAsPristine();
          guardadas++;
        } catch (error) {
          fila.error = 'No se pudo guardar. Los cambios siguen pendientes.';
          if (error instanceof HttpErrorResponse) this.errors.handleError(error);
        }
      }
      for (const fila of this.filas.filter(f => f.produccion?.pendiente)) {
        if (await this.guardarProduccion(fila.produccion!)) guardadas++;
      }
      for (const fresco of this.frescos.filter(frescoPendiente)) {
        if (await this.frescosService.guardar(fresco, this.idAgendamiento)) guardadas++;
      }
    } finally {
      this.saving = false;
      if (this.canEdit) this.observacion.enable({ emitEvent: false });
      this.filas.filter(f => this.permitido(f)).forEach(f => f.form.enable({ emitEvent: false }));
    }
    this.snackbar.showSnackBar(`${guardadas} cambio(s) guardado(s) · ${this.pendientes} pendiente(s)`);
    if (guardadas && !this.pendientes) this.saved.emit();
  }

  async eliminarProduccion(produccion: ProduccionEtapa): Promise<void> {
    if (!produccion.salida || this.saving) return;
    this.saving = true;
    try {
      const response = await firstValueFrom(this.service.destroySalida(this.idAgendamiento, this.cultivo.id, produccion.salida.id));
      if (response.status) {
        this.cultivo.salidas_cultivo = this.cultivo.salidas_cultivo.filter(s => s.id !== produccion.salida!.id);
        produccion.restablecer(null, this.canCreate);
        this.snackbar.showSnackBar(`Producción de embriones ${produccion.nombre} eliminada`);
        this.saved.emit();
      }
    } catch (error) {
      if (error instanceof HttpErrorResponse) this.errors.handleError(error);
    } finally {
      this.saving = false;
    }
  }

  private salidaDe(dia: number, turno: TurnoControlCultivo): SalidaCultivoEmbrionario | null {
    return this.cultivo.salidas_cultivo?.find(s => s.destino === 'V' && s.dia === dia && s.turno === turno) ?? null;
  }

  private async guardarProduccion(produccion: ProduccionEtapa): Promise<boolean> {
    produccion.error = '';
    if (!produccion.lista) {
      produccion.form.markAllAsTouched();
      const faltan = produccion.faltantes;
      produccion.error = faltan.length
        ? `Falta completar: ${faltan.join(', ')}.`
        : 'Complete los datos de la producción (y el OPS o ID de cada pajuela si es congelada).';
      return false;
    }
    const existente = produccion.salida;
    try {
      const response = await firstValueFrom(existente
        ? this.service.updateSalida(this.idAgendamiento, this.cultivo.id, existente.id, produccion.payload())
        : this.service.storeSalida(this.idAgendamiento, this.cultivo.id, produccion.payload()));
      if (!response.status) { produccion.error = 'No se pudo guardar. Los cambios siguen pendientes.'; return false; }
      this.cultivo.salidas_cultivo = [...(this.cultivo.salidas_cultivo ?? []).filter(s => s.id !== response.data.id), response.data];
      produccion.restablecer(response.data, this.canEdit);
      return true;
    } catch (error) {
      produccion.error = 'No se pudo guardar. Los cambios siguen pendientes.';
      if (error instanceof HttpErrorResponse) this.errors.handleError(error);
      return false;
    }
  }

  avanzar(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    const inputs = Array.from(target.closest('app-data-table')?.querySelectorAll<HTMLInputElement>('input:not(:disabled)') ?? []);
    const next = inputs[inputs.indexOf(target) + 1];
    if (next) { event.preventDefault(); next.focus(); }
  }
}
